// src/pages/PrintQueue.jsx
//
// The production team's list of print submissions, /print-queue. Lead
// reviewers and admins only: GET /api/intake/queue is the gate, and this page
// just says so plainly when it refuses. Same dashboard shell as the Review
// Queue. Each row opens the submission page, where the team reviews it.

import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useNavigate } from 'react-router-dom';
import {
  CircularProgress,
  Link,
  Paper,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Typography,
} from '@mui/material';
import Sidebar from '../components/dashboard/Sidebar';
import StatusChip from '../components/intake/StatusChip';
import { intakeApi } from '../components/intake/api';
import { money } from '../utils/intake';
import { FONT } from '../components/intake/intakeSx';
import '../styles/Dashboard.css';
import '../styles/Sidebar.css';

const FILTERS = [
  { id: 'under_review', label: 'Under review' },
  { id: 'changes_requested', label: 'Changes requested' },
  { id: 'approved', label: 'Approved' },
  { id: 'all', label: 'All' },
];

function updatedOn(iso) {
  const when = iso ? new Date(iso) : null;
  return when && !Number.isNaN(when.getTime())
    ? when.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })
    : '—';
}

export default function PrintQueue() {
  const navigate = useNavigate();
  const [status, setStatus] = useState('under_review');
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setData(null);
    setError(null);
    intakeApi
      .queue(status)
      .then((d) => {
        if (!cancelled) setData(d);
      })
      .catch((err) => {
        if (cancelled) return;
        setError(err.status === 403 ? 'The print queue is for the production team.' : err.message);
      });
    return () => {
      cancelled = true;
    };
  }, [status]);

  const counts = (data && data.counts) || {};

  return (
    <div className="dashboard-layout">
      <Sidebar />
      <main className="dashboard-main">
        <h2 className="section-title">Print Queue</h2>

        <Tabs
          value={status}
          onChange={(e, value) => setStatus(value)}
          variant="scrollable"
          allowScrollButtonsMobile
          sx={{ mb: 2 }}
        >
          {FILTERS.map((f) => (
            <Tab
              key={f.id}
              value={f.id}
              label={f.id !== 'all' && counts[f.id] != null ? `${f.label} (${counts[f.id]})` : f.label}
              sx={{ textTransform: 'none' }}
            />
          ))}
        </Tabs>

        {error && (
          <Typography role="alert" color="error">
            {error}
          </Typography>
        )}
        {!error && !data && <CircularProgress />}
        {data && data.submissions.length === 0 && (
          <Typography color="text.secondary">Nothing here right now.</Typography>
        )}
        {data && data.submissions.length > 0 && (
          <TableContainer component={Paper} variant="outlined">
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Reservation</TableCell>
                  <TableCell>Book</TableCell>
                  <TableCell>Author</TableCell>
                  <TableCell align="right">Copies</TableCell>
                  <TableCell align="right">Estimate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Updated</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {data.submissions.map((s) => (
                  <TableRow
                    key={s.reservation_number}
                    hover
                    sx={{ cursor: 'pointer' }}
                    onClick={() => navigate(`/intake/${s.reservation_number}`)}
                    data-testid="print-queue-row"
                  >
                    <TableCell sx={{ fontFamily: FONT.mono, whiteSpace: 'nowrap' }}>
                      {/* A real link too, so the row is reachable from the keyboard. */}
                      <Link
                        component={RouterLink}
                        to={`/intake/${s.reservation_number}`}
                        onClick={(e) => e.stopPropagation()}
                      >
                        {s.reservation_number}
                      </Link>
                    </TableCell>
                    {/* A floor, so a phone scrolls the table sideways instead of
                        breaking titles mid-word. */}
                    <TableCell sx={{ minWidth: 180 }}>{s.title || 'Untitled'}</TableCell>
                    <TableCell>{s.author || '—'}</TableCell>
                    <TableCell align="right">{s.quantity != null ? s.quantity.toLocaleString('en-US') : '—'}</TableCell>
                    <TableCell align="right">{s.total != null ? money(s.total) : '—'}</TableCell>
                    <TableCell>
                      <StatusChip status={s.status} />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{updatedOn(s.updated_at)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </main>
    </div>
  );
}
